<template>
  <main class="p-enteraccount">
    <section class="p-enteraccount__wrap">
      <h2 class="c-regist__title">{{ t('EnterAccountPage.title') }}</h2>
      <RegisterFlow :steps="6" :nowStep="4" v-if="!enterAccountStore.isInvitedUser" />
      <p v-if="enterAccountStore.hasAffiliatedOrganizations" class="c-regist__comment">
        {{ t('EnterAccountPage.messageForMultiOrgCreate') }}
      </p>
      <p v-else-if="enterAccountStore.isInvitedUser" class="c-regist__comment">
        {{ t('EnterAccountPage.messageForInvited', [enterAccountStore.organizationName]) }}
      </p>
      <p v-else class="c-regist__comment">
        {{ t('EnterAccountPage.message') }}
      </p>
      <p class="p-enteraccount__add_team_link" v-if="enterAccountStore.isInvitedUser">
        {{ t('EnterAccountPage.addTeamLinkBefore') }}
        <router-link :to="{ name: 'AddTeamPage', query: { tokenId: enterAccountStore.tokenId } }">
          {{ t('EnterAccountPage.here') }}
        </router-link>
        {{ t('EnterAccountPage.addTeamLinkAfter') }}
      </p>

      <dl class="p-enteraccount__lists">
        <div class="p-enteraccount__list">
          <dt class="p-enteraccount__list-title">
            <span class="-text">{{ t('EnterAccountPage.displayName') }}</span>
            <span class="-required">{{ t('EnterAccountPage.must') }}</span>
          </dt>
          <dd>
            <input-text
              ref="inFieldDisplayName"
              v-model:input-value="enterAccountStore.displayName"
              class="p-enteraccount__list-data-item"
              name="displayName"
              :placeholder="t('EnterAccountPage.displayNameMsg')"
              rules="required|displayNameFormat"
            />
          </dd>
        </div>
        <div v-if="!enterAccountStore.isInvitedUser" class="p-enteraccount__list">
          <dt class="p-enteraccount__list-title">
            <span class="-text">{{ t('EnterAccountPage.telNo') }}</span>
            <span class="-required" v-if="enterAccountStore.isLine">{{
              t('EnterAccountPage.must')
            }}</span>
          </dt>
          <dd>
            <vue-tel-input
              class="p-enteraccount__list-data-item_tel"
              :defaultCountry="defaultIso2"
              :inputOptions="{
                placeholder: t('EnterAccountPage.telNoMsg'),
                type: 'tel',
                validCharactersOnly: true,
              }"
              @input="onInputPhoneNumber"
              @focus="onFocusPhoneNumber"
            ></vue-tel-input>
            <p class="p-inputerror__error" v-if="state.invalidTelNumberMessageKey">
              {{ t(`EnterAccountPage.error${state.invalidTelNumberMessageKey}`) }}
            </p>
          </dd>
        </div>
        <div class="p-enteraccount__list" v-if="!enterAccountStore.hasAffiliatedOrganizations">
          <dt class="p-enteraccount__list-title">
            <span class="-text">{{ t('EnterAccountPage.id') }}</span>
            <span class="-required">{{ t('EnterAccountPage.must') }}</span>
          </dt>
          <dd>
            <input-text
              ref="inFieldUserName"
              v-model:input-value="enterAccountStore.username"
              class="p-enteraccount__list-data-item"
              name="username"
              :placeholder="t('EnterAccountPage.idMsg')"
              rules="required|userIdFormat"
              :otherErrorMessage="
                state.isDuplicateUserId ? [t('EnterAccountPage.alreadyInUseIdMessage')] : []
              "
              @input="
                () => {
                  state.isAvailableUserId = false
                  state.isDuplicateUserId = false
                }
              "
              @delayinput="checkExistUsername"
            />
          </dd>
        </div>
        <div class="p-enteraccount__list" v-if="!enterAccountStore.hasAffiliatedOrganizations">
          <dt class="p-enteraccount__list-title">
            <span class="-text">{{ t('EnterAccountPage.password') }}</span>
            <span class="-required">{{ t('EnterAccountPage.must') }}</span>
            <span class="-comment">{{ t('EnterAccountPage.validate.password') }}</span>
          </dt>
          <dd>
            <input-text
              ref="inFieldPassword"
              v-model:input-value="enterAccountStore.password"
              class="p-enteraccount__list-data-item"
              name="password"
              type="password"
              :placeholder="t('EnterAccountPage.password')"
              rules="required|passwordFormat"
            />
          </dd>
        </div>
        <div class="p-enteraccount__list" v-if="!enterAccountStore.hasAffiliatedOrganizations">
          <dt class="p-enteraccount__list-title">
            <span class="-text">{{ t('EnterAccountPage.password2') }}</span>
            <span class="-required">{{ t('EnterAccountPage.must') }}</span>
          </dt>
          <dd>
            <input-text
              v-model:input-value="state.confirmPassword"
              class="p-enteraccount__list-data-item"
              name="confirmPassword"
              type="password"
              :placeholder="t('EnterAccountPage.password')"
            />
          </dd>
        </div>
      </dl>
      <div class="p-enteraccount__link">
        <button
          @click="onClickEnterAccount"
          :disabled="!isEnableButton"
          :class="{ isDisabled: !isEnableButton }"
          class="c-button"
        >
          <span class="c-button__text">{{ t('EnterAccountPage.confirm') }}</span>
        </button>
      </div>
    </section>
  </main>
</template>

<script lang="ts">
import { defineComponent, reactive, computed, onMounted, nextTick, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import RegisterFlow from '@/components/registerTeam/RegisterFlow.vue'
import StoreUtil from '@/stores/StoreUtil'
import InputText from '@/components/common/field/InputText.vue'
import useMaintenanceStatus from '@/Hooks/useMaintenanceStatus'
import { VueTelInputResult } from 'vue3-tel-input'

export default defineComponent({
  name: 'EnterAccountPage',
  setup() {
    const router = useRouter()
    const state = reactive({
      confirmPassword: '',
      isAvailableUserId: false,
      isDuplicateUserId: false,
      invalidTelNumberMessageKey: null as string | null,
      telNumberStatusObject: {} as VueTelInputResult,
    })
    const enterAccountStore = StoreUtil.useStore('EnterAccountStore')
    const messageDialogStore = StoreUtil.useStore('MessageDialogStore')
    const ErrorStore = StoreUtil.useStore('ErrorStore')
    const { checkMaintenanceStatusByApiBaseUrl } = useMaintenanceStatus()
    const inFieldDisplayName = ref<InstanceType<typeof InputText>>()
    const inFieldUserName = ref<InstanceType<typeof InputText>>()
    const inFieldPassword = ref<InstanceType<typeof InputText>>()
    const isTelNumberValid = computed(
      () =>
        // 「telNumberStatusObject.valid」は未入力でもfalseを返すため、メールアドレスでの登録の際は未入力でもtrueを返すための処理
        (!enterAccountStore.isLine && !state.telNumberStatusObject.formatted) ||
        state.telNumberStatusObject.valid,
    )
    const isEnableButton = computed(() => {
      if (!inFieldDisplayName.value?.meta.valid) return false
      // 電話番号は招待時入力なし、LINEでの新規登録時に必須入力
      if (!enterAccountStore.isInvitedUser && enterAccountStore.isLine && !isTelNumberValid.value) {
        return false
      }
      // 組織所属済みのユーザが新規組織を作成する場合以外はユーザID・パスワードのチェック
      if (
        !enterAccountStore.hasAffiliatedOrganizations &&
        (!inFieldUserName.value?.meta.valid ||
          !inFieldPassword.value?.meta.valid ||
          enterAccountStore.password !== state.confirmPassword ||
          !state.isAvailableUserId ||
          state.isDuplicateUserId)
      ) {
        return false
      }
      return true
    })
    /**
     * 電話番号入力の国コード初期値
     */
    const defaultIso2 = computed(() => {
      return process.env.VUE_APP_ISO2_THIS_REGION
    })
    const { t } = useI18n()
    const checkExistUsername = async () => {
      state.isAvailableUserId = false
      await nextTick()
      if (!inFieldUserName.value?.meta.valid) return
      const result = await enterAccountStore.checkDuplicateUserId()
      if (result.isSuccess) {
        state.isDuplicateUserId = false
        state.isAvailableUserId = true
      } else if (!result.isSuccess && result.res.response.data.error_code === '409_0000') {
        state.isDuplicateUserId = true
        state.isAvailableUserId = false
      } else {
        state.isDuplicateUserId = false
        state.isAvailableUserId = false
      }
    }

    /**
     * 電話番号入力のInputEventHandler
     * @param formattedNumber 入力した電話番号
     * @param object 入力した電話番号の状態オブジェクト
     */
    const onInputPhoneNumber = (formattedNumber: string, object: VueTelInputResult) => {
      // コンポーネントの作りの問題で2回呼び出されそのうち1回がundefinedで入ってくるための対処
      if (!object) return
      state.telNumberStatusObject = object
      if (object.valid) {
        state.invalidTelNumberMessageKey = ''
        enterAccountStore.telNumber = formattedNumber
        enterAccountStore.countryIso2 = object.country.iso2
      }
      // 初期表示で何も入力していないのにエラーが表示されてしまうための対処、onBlurで初期化されnullから’’になる
      else if (state.invalidTelNumberMessageKey !== null) {
        state.invalidTelNumberMessageKey = isTelNumberValid.value ? '' : 'InvalidNumber'
      }
    }
    /**
     * 電話番号入力のFocusEventHandler
     */
    const onFocusPhoneNumber = () => {
      // コンポーネントの問題でMountedで@inputが走ってしまいいきなりエラー表示が行われるための対処。Focusするまでnullとすることで回避
      if (state.invalidTelNumberMessageKey === null) state.invalidTelNumberMessageKey = ''
    }
    onMounted(async () => {
      const isAvailable = await checkMaintenanceStatusByApiBaseUrl()
      if (!isAvailable) {
        router.replace({ name: 'MaintenancePage' })
        return
      }

      const isInvitedUser = router.currentRoute.value.path === '/invite'
      enterAccountStore.isInvitedUser = isInvitedUser
      enterAccountStore.isExistingAccount = false
      if (typeof router.currentRoute.value.query.tokenId === 'string') {
        enterAccountStore.tokenId = router.currentRoute.value.query.tokenId
      } else if (window.location.search) {
        // LINE認可画面からのリダイレクト（クエリはhashより前のsearch部に付く）。
        // FLUXBACK-2532 の恒久対応まで LINE 登録を停止しているため、認可コードの有無にかかわらず LINE 連携のキャンセルと同じ扱いにする。
        await messageDialogStore.openWithSettings({
          title: t('EnterAccountPage.lineIdConnectCanceled.title'),
          htmlMessage: t('EnterAccountPage.lineIdConnectCanceled.message'),
          status: 'error',
        })
        router.replace({ name: 'ErrorPage' })
        return
      } else {
        ErrorStore.setConfig(`tokenInvalid.${isInvitedUser ? 'invite' : 'register'}`)
        router.replace({ name: 'ErrorPage' })
        return
      }

      const validationResult = await enterAccountStore.validateToken()
      if (!validationResult.isSuccess || !validationResult.res.data.isValid) {
        const displayMessage = isInvitedUser
          ? t('EnterAccountPage.urlInvalidError.messageForInvite')
          : t('EnterAccountPage.urlInvalidError.messageForRegister')
        await messageDialogStore.openWithSettings({
          title: t('EnterAccountPage.urlInvalidError.title'),
          message: displayMessage,
          status: 'error',
        })
        ErrorStore.setConfig(`tokenInvalid.${isInvitedUser ? 'invite' : 'register'}`)
        router.replace({ name: 'ErrorPage' })
      }
      // 招待されたユーザーだったら招待元の組織名を設定
      enterAccountStore.organizationName = enterAccountStore.isInvitedUser
        ? validationResult.res.data?.manageOrganization?.organizationName
        : ''
      // 組織種別を設定
      enterAccountStore.organizationType =
        validationResult.res.data?.manageRegistToken?.organizationType ||
        validationResult.res.data?.manageOrganization?.organizationType
      enterAccountStore.courseId =
        validationResult.res.data?.manageRegistToken?.courseId ||
        validationResult.res.data?.manageOrganization?.courseId
      // 新規組織作成時にですでに所属済み組織があるかどうかを判定
      enterAccountStore.hasAffiliatedOrganizations =
        validationResult.res.data?.manageRegistToken?.affiliatedOrganizations?.length > 0
      if (validationResult.res.data?.manageRegistToken?.additionalData?.offlinePlan)
        enterAccountStore.isOfflinePlan = true
      await checkExistUsername()
    })
    return {
      state,
      enterAccountStore,
      isEnableButton,
      checkExistUsername,
      t,
      inFieldDisplayName,
      inFieldUserName,
      inFieldPassword,
      onClickEnterAccount() {
        if (enterAccountStore.isInvitedUser) {
          router.replace({ name: 'InviteConfirmationPage' })
          return
        }
        router.replace({ name: 'ConfirmationPage' })
      },
      onInputPhoneNumber,
      onFocusPhoneNumber,
      defaultIso2,
    }
  },
  components: {
    RegisterFlow,
    InputText,
  },
})
</script>

<style scoped lang="scss">
.isDisabled {
  opacity: 0.3;
  pointer-events: none;
}
</style>
